import { AxiosError } from 'axios';
import { get, camelCase } from 'lodash';
import { useMutation, useQueryClient } from 'react-query';
import React, { useState, useContext, useMemo, useRef } from 'react';

import { Request } from '../Records/index';
import RespondentContext from '../Context/Respondent';
import { alertAsync } from '~/App/Components/Modals/Alert';
import { FileRequestType } from '~/types/models/testimonial';
import {
  uploadAttachmentUrl,
  UploadAttachmentUrlParams,
  deleteAttachment,
  DeleteAttachmentParams,
  updateAttachment,
  UpdateAttachmentParams,
} from '../Api/TestimonialRespondent';
import uploadToS3 from '~/App/Api/S3Upload';
import { getFileExtension } from '~/utils/file';

import UploadFileItem, { UploadFileItemProps } from './UploadFileItem';
import { useTranslation } from 'react-i18next';

function getRequestFileTitle(fileRequest: Request) {
  const { t } = useTranslation();
  switch (fileRequest.get('type') as FileRequestType) {
    case FileRequestType.CompanyLogo:
    case FileRequestType.ProfilePhoto:
      return t(
        `testimonial:additionalInfo.form.${camelCase(fileRequest.get('type'))}`
      );
    default:
      return fileRequest.get('label', 'Custom File');
  }
}

const errorCallback = (error: AxiosError) => {
  const errorMessage =
    error?.response?.data?.error?.message ??
    'Unkown error. Something went wrong while processing your request.';
  alertAsync(
    <>
      <p>{errorMessage}</p>
      <p>Please refresh the page and try again.</p>
    </>
  );
};

export interface RequestFilesItemProps
  extends Pick<UploadFileItemProps, 'disableFileUpload'> {
  request: Request;
  fontColor?: string;
  fontFamily?: string;
  ctaColor?: string;
  ctaCornerRadius?: number;
}

export default function RequestFilesItem({
  request,
  disableFileUpload = false,
  fontColor,
  fontFamily,
  ctaColor,
  ctaCornerRadius,
}: RequestFilesItemProps) {
  const { respondent, setRespondent } = useContext(RespondentContext);
  const [fileName, setFileName] = useState<string | undefined>(
    get(request.get('response'), ['media_metadata', 'name'])
  );
  const queryClient = useQueryClient();

  const fileRef = useRef<HTMLInputElement | null>(null);

  const clearFile = () => {
    setFileName('');
    if (fileRef.current) {
      fileRef.current.value = '';
    }
  };

  const { mutateAsync: uploadAttachmentUrlMutation, isLoading: isUploading } =
    useMutation(
      (uploadUrlParams: UploadAttachmentUrlParams) =>
        uploadAttachmentUrl(uploadUrlParams),
      {
        onSuccess: () => {
          queryClient.invalidateQueries('result');
        },
      }
    );

  const { mutateAsync: deleteAttachmentMutation, isLoading: isDeleting } =
    useMutation(
      (deleteAttachmentParams: DeleteAttachmentParams) =>
        deleteAttachment(deleteAttachmentParams),
      {
        onSuccess: () => {
          setRespondent?.(
            respondent.setRequestResponse(request.get('uuid'), undefined)
          );
        },
      }
    );
  const { mutateAsync: updateAttachmentMutation } = useMutation(
    (updateAttachmentParams: UpdateAttachmentParams) =>
      updateAttachment(updateAttachmentParams),
    {
      onSuccess: (res) => {
        setRespondent?.(
          respondent.setRequestResponse(request.get('uuid'), res.data)
        );
      },
    }
  );

  const isLoading = useMemo(
    () => isUploading || isDeleting,
    [isUploading, isDeleting]
  );

  const disableDelete = useMemo(() => {
    return !!fileName && !get(request.get('response'), 'uuid');
  }, []);

  const onFileDelete = async () => {
    try {
      await deleteAttachmentMutation({
        respondentId: respondent.get('uuid'),
        requestResponseId: get(request.get('response'), 'uuid'),
      });
      clearFile();
    } catch (error) {
      errorCallback(error as AxiosError);
    }
  };

  const onFileChange = async (fileType: string, fileSelected: File) => {
    try {
      setFileName(fileSelected.name);

      const uploadUrl = await uploadAttachmentUrlMutation({
        respondentId: respondent.get('uuid'),
        attachmentId: request.get('uuid'),
        extension: getFileExtension(fileSelected),
        metadata: {
          name: fileSelected.name,
          size: fileSelected.size.toString(),
        },
      });

      if (uploadUrl) {
        await uploadToS3(uploadUrl.url, fileSelected, fileType);

        updateAttachmentMutation({
          respondentId: respondent.get('uuid'),
          requestResponseId: uploadUrl.uuid,
        });
      }
    } catch (error) {
      errorCallback(error as AxiosError);
      clearFile();
    }
  };

  const { t } = useTranslation();

  return (
    <UploadFileItem
      ref={fileRef}
      title={getRequestFileTitle(request)}
      note={t('testimonial:additionalInfo.form.formatter')}
      fileName={fileName}
      isLoading={isLoading}
      onFileChange={onFileChange}
      onFileDelete={onFileDelete}
      disableFileUpload={disableFileUpload}
      disableDelete={disableDelete}
      fontColor={fontColor}
      fontFamily={fontFamily}
      ctaColor={ctaColor}
      ctaCornerRadius={ctaCornerRadius}
    />
  );
}
